/**
 * Stage Finance workspace — cash timeline from the Stage Finance engine.
 * Consumes ledger + selectors only; no independent finance math.
 */
import { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  MODES,
  hydrateAssumptions,
  linkConstructionPaymentTranches,
  linkLandAcquisitionMonth,
  linkSaleMonthOffsets,
  linkSalesAllocation,
  programmeBoundsFromDurations,
  runStageFinance,
} from '../stageFinance';
import { DEFAULT_ASSUMPTIONS } from '../stageFinance/defaults';

function money(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Math.round(Number(v));
  const abs = Math.abs(n).toLocaleString('en-US');
  return n < 0 ? `−${abs}` : abs;
}

function axisFmt(v) {
  const m = v / 1e6;
  if (Math.abs(m) >= 10) return `${m.toFixed(0)}m`;
  if (Math.abs(m) >= 1) return `${m.toFixed(1)}m`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return `${Math.round(v)}`;
}

function Segmented({ value, options, onChange, testId }) {
  return (
    <div className="inline-flex rounded-lg border border-black/10 bg-white p-0.5" data-testid={testId}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`rounded-md px-2.5 py-1.5 text-[12px] transition-colors ${
              active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-black/5'
            }`}
            data-testid={`${testId}-${opt.id}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ConclusionCard({ label, value, detail, danger = false, testId }) {
  return (
    <div className="rounded-xl border border-black/8 bg-white p-3" data-testid={testId}>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`mt-1 text-[20px] font-semibold tabular-nums ${danger ? 'text-rose-700' : 'text-slate-900'}`}>
        {value}
      </div>
      {detail ? <div className="mt-1 text-[11px] text-slate-500">{detail}</div> : null}
    </div>
  );
}

export function StageFinanceView({
  outputs,
  assumptions: savedAssumptions = null,
  onAssumptionsChange,
  onOpenPnL,
}) {
  const [localAssumptions, setLocalAssumptions] = useState(() => (
    hydrateAssumptions({ ...DEFAULT_ASSUMPTIONS, ...(savedAssumptions || {}) })
  ));
  const [timingHint, setTimingHint] = useState(null);

  const assumptions = useMemo(
    () => hydrateAssumptions(
      savedAssumptions
        ? { ...DEFAULT_ASSUMPTIONS, ...savedAssumptions }
        : localAssumptions,
    ),
    [savedAssumptions, localAssumptions],
  );

  const setAssumptions = (next) => {
    const resolved = typeof next === 'function' ? next(assumptions) : next;
    const hydrated = hydrateAssumptions(resolved);
    if (onAssumptionsChange) onAssumptionsChange(hydrated);
    else setLocalAssumptions(hydrated);
  };

  const result = useMemo(
    () => runStageFinance(outputs || {}, assumptions),
    [outputs, assumptions],
  );

  const bounds = useMemo(
    () => programmeBoundsFromDurations(assumptions),
    [assumptions],
  );

  const conclusions = result.conclusions || {};
  const stages = result.stages || [];
  const facility = result.facilityAnalysis || {};
  const ledgerTotals = result.ledger?.totals || {};
  const isQuotePart = assumptions.mode === MODES.quotePart;

  const chartData = useMemo(() => {
    const chart = result.chart || { bars: [], line: [] };
    const byMonth = new Map();
    for (const b of chart.bars || []) {
      byMonth.set(b.month, {
        month: b.month,
        stage: b.stage,
        spvIn: b.spvIn || 0,
        bankPartnerIn: b.bankPartnerIn || 0,
        buyerIn: b.buyerIn || 0,
        costsOut: b.costsOut || 0,
        repaymentsOut: b.repaymentsOut || 0,
      });
    }
    for (const l of chart.line || []) {
      const row = byMonth.get(l.month) || { month: l.month };
      row.closingSpvCash = l.closingSpvCash;
      row.salesSecured = l.salesSecured || 0;
      byMonth.set(l.month, row);
    }
    return [...byMonth.values()].sort((a, b) => a.month - b.month);
  }, [result.chart]);

  const lowest = conclusions.lowestCash;
  const funding = conclusions.additionalFunding;
  const peak = conclusions.peakSponsorCash;
  const completion = conclusions.completionCash;

  const saleTimingFields = [
    ['preBankEndOffsetFromAuthorization', 'Pre-bank sale month after permit'],
    ['postBankEndOffsetFromAuthorization', 'Post-bank sale month after permit'],
    ['lateEndOffsetFromAuthorization', 'Late sale month after permit'],
  ];

  return (
    <div
      className="flex-1 min-w-0 overflow-y-auto bg-[#f5f5f7] px-3 py-4 md:px-6 md:py-5"
      data-testid="stage-finance-workspace"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif' }}
    >
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Stage Finance</h1>
            <p className="mt-0.5 text-[13px] text-slate-500">
              Cash in, cash out, and what must still be secured to survive each stage.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={assumptions.mode}
              onChange={(mode) => setAssumptions({ ...assumptions, mode })}
              testId="stage-finance-mode"
              options={[
                { id: MODES.venteATerme, label: 'Vente à terme' },
                { id: MODES.quotePart, label: 'Quote-part' },
              ]}
            />
            {onOpenPnL ? (
              <button
                type="button"
                onClick={onOpenPnL}
                className="h-8 rounded-lg border border-black/10 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-black/5"
                data-testid="stage-finance-open-pnl"
              >
                Back to P&L
              </button>
            ) : null}
          </div>
        </div>

        {result.incomplete ? (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
            data-testid="stage-finance-incomplete"
          >
            <div className="font-semibold">
              {result.programme
                ? 'Stage Finance cannot build a schedule yet'
                : 'P&L incomplete — Stage Finance cannot build a ledger yet'}
            </div>
            <ul className="mt-1 list-disc pl-5 text-[12px]">
              {(result.incompleteReasons || []).map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {(!result.incomplete || result.programme) ? (
          <>
            {/* 1. Controls — kept visible when programme is too short so durations can be fixed */}
            <div
              className="rounded-xl border border-black/8 bg-white p-3 space-y-3"
              data-testid="stage-finance-controls"
            >
              <div className="text-[13px] font-semibold text-slate-800">Simulation controls</div>
              <div className="grid gap-3 md:grid-cols-3" data-testid="stage-finance-sales-allocation">
                {[
                  ['preBank', 'Pre-bank sales'],
                  ['postBank', 'Post-bank sales'],
                  ['late', 'Late sales'],
                ].map(([key, label]) => (
                  <label key={key} className="block text-[11px] text-slate-600">
                    <div className="mb-1 flex justify-between">
                      <span>{label}</span>
                      <span className="tabular-nums">{Math.round((assumptions.salesAllocation?.[key] || 0) * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={Math.round((assumptions.salesAllocation?.[key] || 0) * 100)}
                      data-testid={`stage-finance-alloc-${key}`}
                      onChange={(e) => {
                        const next = linkSalesAllocation(
                          assumptions.salesAllocation,
                          key,
                          Number(e.target.value) / 100,
                        );
                        setAssumptions({ ...assumptions, salesAllocation: next });
                      }}
                      className="w-full"
                    />
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-slate-500" data-testid="stage-finance-sales-allocation-total">
                Allocation total {Math.round(((assumptions.salesAllocation?.preBank || 0) + (assumptions.salesAllocation?.postBank || 0) + (assumptions.salesAllocation?.late || 0)) * 100)}%
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="stage-finance-phase-timing">
                {saleTimingFields.map(([key, label]) => {
                  const offset = assumptions.salesPhaseTiming?.[key] ?? 0;
                  const calendarMonth = bounds.permitMonth + offset;
                  return (
                    <label key={key} className="block text-[11px] text-slate-600">
                      <span>{label}</span>
                      <input
                        type="number"
                        min={0}
                        max={bounds.maxSaleOffset}
                        className="mt-1 h-8 w-full rounded-md border border-black/10 px-2 text-[12px]"
                        value={offset}
                        data-testid={`stage-finance-timing-${key}`}
                        onChange={(e) => {
                          const linked = linkSaleMonthOffsets(
                            assumptions.salesPhaseTiming,
                            key,
                            Number(e.target.value),
                            bounds,
                          );
                          setTimingHint(linked.message);
                          setAssumptions({
                            ...assumptions,
                            salesPhaseTiming: linked.salesPhaseTiming,
                          });
                        }}
                      />
                      <span className="mt-0.5 block text-[10px] text-slate-400" data-testid={`stage-finance-timing-calendar-${key}`}>
                        → calendar M{calendarMonth} (permit M{bounds.permitMonth}, max +{bounds.maxSaleOffset})
                      </span>
                    </label>
                  );
                })}
                <label className="block text-[11px] text-slate-600">
                  <span>Land acquisition month</span>
                  <input
                    type="number"
                    min={1}
                    max={bounds.maxMonth}
                    className="mt-1 h-8 w-full rounded-md border border-black/10 px-2 text-[12px]"
                    value={assumptions.landAcquisitionMonth ?? ''}
                    placeholder={`auto (M${bounds.defaultAcquisition})`}
                    data-testid="stage-finance-land-acquisition-month"
                    onChange={(e) => {
                      const linked = linkLandAcquisitionMonth(
                        e.target.value === '' ? null : Number(e.target.value),
                        bounds,
                      );
                      setTimingHint(linked.message);
                      setAssumptions({
                        ...assumptions,
                        landAcquisitionMonth: linked.landAcquisitionMonth,
                      });
                    }}
                  />
                  <span className="mt-0.5 block text-[10px] text-slate-400" data-testid="stage-finance-land-acquisition-effective">
                    Engine uses M{result.programme?.acquisitionMonth ?? bounds.defaultAcquisition}
                    {' '}(valid M1–M{bounds.maxMonth})
                  </span>
                </label>
              </div>
              {timingHint ? (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
                  data-testid="stage-finance-timing-hint"
                >
                  {timingHint}
                </div>
              ) : null}

              <details className="space-y-2 rounded-lg border border-black/8 bg-white/60 px-3 py-2" data-testid="stage-finance-payment-timing">
                <summary className="cursor-pointer list-none text-[12px] font-semibold text-slate-700">
                  Payment timing
                  <span className="ml-2 font-normal text-[11px] text-slate-500">
                    Stage Finance only — when cash moves
                  </span>
                </summary>
                <div className="text-[11px] text-slate-500 pt-1">
                  Fees under Funding &amp; Capital are sponsor funding (with Equity), not an expense.
                  Feasibility is a genuine prep cost. P&amp;L totals stay the source of truth.
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="block text-[11px] text-slate-600">
                    <div className="mb-1 flex justify-between">
                      <span>Land deposit at signing (M1)</span>
                      <span className="tabular-nums">{Math.round((assumptions.landDepositPct || 0) * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={Math.round((assumptions.landDepositPct || 0) * 100)}
                      data-testid="stage-finance-land-deposit-pct"
                      onChange={(e) => setAssumptions({
                        ...assumptions,
                        landDepositPct: Number(e.target.value) / 100,
                      })}
                      className="w-full"
                    />
                  </label>
                  <label className="block text-[11px] text-slate-600">
                    <span>Construction start delay (mo after auth)</span>
                    <input
                      type="number"
                      min={0}
                      max={60}
                      className="mt-1 h-8 w-full rounded-md border border-black/10 px-2 text-[12px]"
                      value={assumptions.constructionStartOffsetFromAuth ?? 2}
                      data-testid="stage-finance-construction-start-offset"
                      onChange={(e) => setAssumptions({
                        ...assumptions,
                        constructionStartOffsetFromAuth: Number(e.target.value),
                      })}
                    />
                  </label>
                </div>
                <div data-testid="stage-finance-construction-tranches">
                  <div className="mb-1 text-[11px] text-slate-600">
                    Construction payment tranches (Build + Reserve)
                  </div>
                  <div className="grid gap-2 grid-cols-5">
                    {(assumptions.constructionPaymentTranches || []).map((share, idx) => (
                      <label key={`tranche-${idx}`} className="block text-[10px] text-slate-600">
                        <div className="mb-0.5 flex justify-between gap-1">
                          <span>T{idx + 1}</span>
                          <span className="tabular-nums">{Math.round((share || 0) * 100)}%</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={Math.round((share || 0) * 100)}
                          data-testid={`stage-finance-construction-tranche-${idx}`}
                          onChange={(e) => {
                            const next = linkConstructionPaymentTranches(
                              assumptions.constructionPaymentTranches,
                              idx,
                              Number(e.target.value) / 100,
                            );
                            setAssumptions({ ...assumptions, constructionPaymentTranches: next });
                          }}
                          className="w-full"
                        />
                      </label>
                    ))}
                  </div>
                  <div
                    className="mt-1 text-[11px] text-slate-500"
                    data-testid="stage-finance-construction-tranches-total"
                  >
                    Tranche total {Math.round(
                      (assumptions.constructionPaymentTranches || []).reduce((s, v) => s + (v || 0), 0) * 100,
                    )}%
                  </div>
                </div>
              </details>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="stage-finance-durations">
                {[
                  ['prepMonths', 'Prep months'],
                  ['authMonths', 'Auth months'],
                  ['constructionMonths', 'Construction months'],
                  ['deliveryBufferMonths', 'Delivery buffer'],
                ].map(([key, label]) => (
                  <label key={key} className="block text-[11px] text-slate-600">
                    <span>{label}</span>
                    <input
                      type="number"
                      min={0}
                      max={120}
                      className="mt-1 h-8 w-full rounded-md border border-black/10 px-2 text-[12px]"
                      value={assumptions[key]}
                      data-testid={`stage-finance-${key}`}
                      onChange={(e) => setAssumptions({ ...assumptions, [key]: Number(e.target.value) })}
                    />
                  </label>
                ))}
              </div>
              <label className="block max-w-sm text-[11px] text-slate-600">
                <div className="mb-1 flex justify-between">
                  <span>Bank threshold</span>
                  <span className="tabular-nums">{Math.round((assumptions.bankActivationThreshold || 0) * 100)}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round((assumptions.bankActivationThreshold || 0) * 100)}
                  data-testid="stage-finance-bank-threshold"
                  onChange={(e) => setAssumptions({
                    ...assumptions,
                    bankActivationThreshold: Number(e.target.value) / 100,
                  })}
                  className="w-full"
                />
              </label>
            </div>

            {!result.incomplete ? (
              <>
            {/* 2. Dashboard cards */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="stage-finance-conclusions">
              <ConclusionCard
                label="Lowest cash"
                value={money(lowest?.amount)}
                detail={lowest ? `Month ${lowest.month} · ${lowest.stage}` : null}
                danger={lowest != null && lowest.amount < 0}
                testId="stage-finance-lowest-cash"
              />
              <ConclusionCard
                label="Additional funding"
                value={money(funding?.amount)}
                detail={
                  funding?.amount > 0
                    ? `Bank gap ${money(funding.bankFundingGap)} · Sponsor gap ${money(funding.cashGap)} · ${funding.source} by month ${funding.month}`
                    : 'No extra funding gap'
                }
                danger={funding != null && funding.amount > 0}
                testId="stage-finance-additional-funding"
              />
              <ConclusionCard
                label="Peak sponsor cash"
                value={money(peak?.amount)}
                detail={peak ? `Through month ${peak.month}` : null}
                testId="stage-finance-peak-sponsor"
              />
              <ConclusionCard
                label="Completion cash"
                value={money(completion?.cashRemainingInSpv)}
                detail={
                  completion?.bridge
                    ? [
                      `Contributions ${money(completion.bridge.sponsorContributions)}`,
                      `${(completion.bridge.pnlNetProfit || 0) < 0 ? '−' : '+'} P&L result ${money(Math.abs(completion.bridge.pnlNetProfit || 0))}`,
                      completion.bridge.feasibilityPaid > 0
                        ? `− feasibility ${money(completion.bridge.feasibilityPaid)}`
                        : null,
                      completion.bridge.cashAlreadyDistributed > 0
                        ? `− distributed ${money(completion.bridge.cashAlreadyDistributed)}`
                        : null,
                      `= ${money(completion.bridge.expectedCompletionCash)}`,
                    ].filter(Boolean).join(' ')
                    : null
                }
                testId="stage-finance-completion-cash"
              />
            </div>

            {isQuotePart ? (
              <div
                className="grid gap-3 rounded-xl border border-black/8 bg-white p-3 sm:grid-cols-3"
                data-testid="stage-finance-quote-part-split"
              >
                <div>
                  <div className="text-[11px] text-slate-500">Gross property sales</div>
                  <div className="mt-1 text-[18px] font-semibold tabular-nums">
                    {money(ledgerTotals.grossSalesValue)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Buyer construction — outside SPV</div>
                  <div className="mt-1 text-[18px] font-semibold tabular-nums text-blue-700">
                    {money(ledgerTotals.quotePartDirectConstructionContract)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Land + mise en valeur — SPV entitlement</div>
                  <div className="mt-1 text-[18px] font-semibold tabular-nums text-emerald-700">
                    {money(ledgerTotals.quotePartSpvSaleEntitlement)}
                  </div>
                </div>
              </div>
            ) : null}

            {/* 3. Chart */}
            <div className="rounded-xl border border-black/8 bg-white p-3" data-testid="stage-finance-chart">
              <div className="mb-2">
                <div className="text-[13px] font-semibold text-slate-800">SPV cash over time</div>
                <div className="text-[11px] text-slate-500">
                  Roll-up of cash events plus derived bank / partner draws and repayments; lines are closing SPV cash and sales secured.
                  {!isQuotePart && ledgerTotals.totalProjectedConstructionFacilityDraw > 0
                    ? ' Bank inflows include the VAT construction draws still marked “to secure” above.'
                    : ''}
                </div>
              </div>
              <div className="h-[320px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tickFormatter={(m) => `M${m}`} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={axisFmt} tick={{ fontSize: 11 }} width={52} />
                    <Tooltip
                      formatter={(value, name) => [money(value), name]}
                      labelFormatter={(m) => `Month ${m}`}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Bar dataKey="spvIn" name="SPV in" stackId="in" fill="#0f172a" />
                    <Bar dataKey="bankPartnerIn" name="Bank / partner" stackId="in" fill="#2563eb" />
                    <Bar dataKey="buyerIn" name="Cash received by SPV" stackId="in" fill="#059669" />
                    <Bar dataKey="costsOut" name="Costs" stackId="out" fill="#94a3b8" />
                    <Bar dataKey="repaymentsOut" name="Repayments" stackId="out" fill="#f59e0b" />
                    <Line
                      type="monotone"
                      dataKey="closingSpvCash"
                      name="Closing cash"
                      stroke="#e11d48"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="salesSecured"
                      name="Sales secured"
                      stroke="#0d9488"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 4. Stage statement — reconciliation only */}
            <div className="rounded-xl border border-black/8 bg-white overflow-hidden" data-testid="stage-finance-stage-ledger">
              <div className="border-b border-black/5 px-3 py-2">
                <div className="text-[13px] font-semibold text-slate-800">Stage statement</div>
                <div className="text-[11px] text-slate-500" data-testid="stage-finance-equation-subtitle">
                  Opening + cash in − payments due − debt repaid = closing
                  {isQuotePart ? '; direct land-bank repayments stay outside SPV cash' : ''}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[12px]">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Stage</th>
                      <th className="px-3 py-2 font-medium text-right">Opening cash</th>
                      <th className="px-3 py-2 font-medium text-right">Cash in</th>
                      <th className="px-3 py-2 font-medium text-right">Payments due</th>
                      <th className="px-3 py-2 font-medium text-right">Debt repaid</th>
                      <th className="px-3 py-2 font-medium text-right">Closing cash</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((row) => (
                      <tr key={row.stage} className="border-t border-black/5 align-top tabular-nums">
                        <td className="px-3 py-2 capitalize text-slate-800">{row.stage}</td>
                        <td className="px-3 py-2 text-right">{money(row.openingCash)}</td>
                        <td className="px-3 py-2 text-right">
                          {money(row.cashInTotal ?? ((row.spvIn || 0) + (row.bankPartnersIn || 0) + (row.buyerCashIn || 0)))}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {money(row.paymentsDue ?? row.costsOut)}
                        </td>
                        <td className="px-3 py-2 text-right">{money(row.debtRepaid ?? row.repayments)}</td>
                        <td className="px-3 py-2 text-right font-medium">{money(row.closingCash)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 5. Cash events — sole detailed audit ledger */}
            <div className="rounded-xl border border-black/8 bg-white overflow-hidden" data-testid="stage-finance-cash-events">
              <div className="border-b border-black/5 px-3 py-2">
                <div className="text-[13px] font-semibold text-slate-800">Cash events</div>
                <div className="text-[11px] text-slate-500">
                  Sole detailed audit ledger — each row shows the movement and the cumulative position after it.
                  {result.saleMode === 'price_list'
                    ? ' Sales use price-list units.'
                    : result.saleMode === 'price_list_partial'
                      ? ' Sales mix price-list units with an explicit residual assumption.'
                      : ' Sales use three milestone assumptions.'}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[12px]">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium" rowSpan={2}>Month</th>
                      <th className="px-3 py-2 font-medium" rowSpan={2}>Event</th>
                      <th className="px-3 py-2 font-medium text-right" rowSpan={2}>Amount</th>
                      <th className="px-3 py-2 font-medium" rowSpan={2}>Payer → recipient</th>
                      <th className="px-3 py-2 font-medium" rowSpan={2}>Source</th>
                      <th
                        className="px-3 py-2 font-medium text-center border-l border-black/10"
                        colSpan={7}
                        data-testid="cash-events-position-group"
                      >
                        Position after this event
                      </th>
                    </tr>
                    <tr>
                      <th className="px-2 py-1.5 font-medium text-right border-l border-black/10 normal-case tracking-normal">SPV cash</th>
                      <th className="px-2 py-1.5 font-medium text-right normal-case tracking-normal">Bank debt</th>
                      <th className="px-2 py-1.5 font-medium text-right normal-case tracking-normal">Buyer paid</th>
                      <th className="px-2 py-1.5 font-medium text-right normal-case tracking-normal">Construction</th>
                      <th className="px-2 py-1.5 font-medium text-right normal-case tracking-normal">Consultants</th>
                      <th className="px-2 py-1.5 font-medium text-right normal-case tracking-normal">Brokers / sales</th>
                      <th className="px-2 py-1.5 font-medium text-right normal-case tracking-normal">Land / other</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.cashEvents || []).map((ev) => {
                      const pos = ev.positionAfter || {};
                      const conditions = ev.meta?.unmetBankConditions || [];
                      const gap = ev.meta?.fundingGap;
                      return (
                        <tr
                          key={ev.id}
                          className="border-t border-black/5 align-top tabular-nums"
                          data-testid={`stage-finance-cash-event-${ev.id}`}
                        >
                          <td className="px-3 py-1.5 text-slate-800">M{ev.month}</td>
                          <td className="px-3 py-1.5 text-slate-800">
                            {ev.label}
                            {ev.recurring ? (
                              <span className="ml-1 text-[10px] text-slate-500">monthly</span>
                            ) : null}
                            {conditions.length || (gap != null && gap > 0) ? (
                              <div className="mt-0.5 text-[10px] text-amber-700 normal-case tracking-normal">
                                {conditions.length ? conditions.join('; ') : null}
                                {gap != null && gap > 0
                                  ? `${conditions.length ? ' · ' : ''}Gap ${money(gap)}`
                                  : null}
                              </div>
                            ) : null}
                          </td>
                          <td className={`px-3 py-1.5 text-right font-medium ${ev.direction === 'out' ? 'text-slate-700' : 'text-emerald-700'}`}>
                            {ev.direction === 'out' ? '−' : '+'}{money(ev.amount)}
                          </td>
                          <td className="px-3 py-1.5 text-[11px] text-slate-600 normal-case tracking-normal">
                            {(ev.payer || '—').replace(/_/g, ' ')} → {(ev.recipient || '—').replace(/_/g, ' ')}
                          </td>
                          <td className="px-3 py-1.5 text-[11px] text-slate-600 normal-case tracking-normal">
                            {ev.meta?.nonCash
                              ? 'Non-cash'
                              : ev.source === 'pnl'
                                ? 'P&L'
                                : ev.source === 'price_list'
                                  ? 'Price list'
                                  : ev.source === 'ledger'
                                    ? 'Ledger'
                                    : 'Assumption'}
                          </td>
                          <td className="px-2 py-1.5 text-right border-l border-black/10 text-slate-800" data-testid={`pos-spv-${ev.id}`}>
                            {money(pos.spvCash)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800" data-testid={`pos-bank-${ev.id}`}>
                            {money(pos.bankDebt)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800" data-testid={`pos-buyer-${ev.id}`}>
                            {money(pos.buyerPaid)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800" data-testid={`pos-construction-${ev.id}`}>
                            {money(pos.constructionReceived)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800" data-testid={`pos-consultants-${ev.id}`}>
                            {money(pos.consultantsReceived)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800" data-testid={`pos-brokers-${ev.id}`}>
                            {money(pos.brokersReceived)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800" data-testid={`pos-land-${ev.id}`}>
                            {money(pos.landOtherReceived)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {assumptions.mode === MODES.venteATerme ? (
              <div
                className="rounded-xl border border-black/8 bg-white px-3 py-3 text-[12px] text-slate-700"
                data-testid="stage-finance-facility"
              >
                <div className="text-[13px] font-semibold text-slate-800">Construction facility</div>
                <div className="mt-1 grid gap-1 sm:grid-cols-2 lg:grid-cols-5">
                  <div>
                    Required:{' '}
                    <strong className="tabular-nums">{money(facility.constructionFacilityRequired)}</strong>
                  </div>
                  <div>
                    Entered in P&amp;L:{' '}
                    <strong className="tabular-nums">{money(facility.constructionFacilitySecured)}</strong>
                  </div>
                  <div>
                    Still to secure:{' '}
                    <strong className="tabular-nums">{money(facility.remainingBankFundingGap)}</strong>
                  </div>
                  <div>
                    Available by month:{' '}
                    <strong>{facility.facilityMustBeAvailableMonth ?? '—'}</strong>
                  </div>
                  <div>
                    Planned draws:{' '}
                    <strong className="tabular-nums">{money(ledgerTotals.totalProjectedConstructionFacilityDraw)}</strong>
                  </div>
                </div>
              </div>
            ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
